/**
 * Buildings API service for Whakahaere Safety Management System
 *
 * Migrated to typed `@tiaki/http-client` per ADR 113 §Decision.
 */

import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

export interface Building {
  id: number;
  name: string;
  building: string;
  school: string;
  status?: string;
  organization: string;
  latitude?: number | null;
  longitude?: number | null;
  elevation?: number | null;
  data?: Record<string, unknown> | null;
}

export interface BuildingListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Building[];
}

export interface BuildingFilters {
  school?: number;
  search?: string;
  is_active?: boolean;
  page?: number;
  page_size?: number;
}

/**
 * Get list of Buildings with optional filters
 */
export async function getBuildings(
  filters: BuildingFilters = {},
): Promise<BuildingListResponse> {
  const result = await client.GET('/api/v2/building/', {
    params: { query: filters as never },
  });
  return unwrap(result) as unknown as BuildingListResponse;
}

/**
 * Get a single Building by ID
 */
export async function getBuildingById(id: number): Promise<Building> {
  const result = await client.GET('/api/v2/building/{id}/', {
    params: { path: { id } as never },
  });
  return unwrap(result) as unknown as Building;
}

/**
 * Create a new Building
 */
export async function createBuilding(
  data: Omit<Building, 'id'>,
): Promise<Building> {
  const result = await client.POST('/api/v2/building/', {
    body: data as never,
  });
  return unwrap(result) as unknown as Building;
}

/**
 * Update an existing Building
 */
export async function updateBuilding(
  id: number,
  data: Partial<Omit<Building, 'id'>>,
): Promise<Building> {
  const result = await client.PATCH('/api/v2/building/{id}/', {
    params: { path: { id } as never },
    body: data as never,
  });
  return unwrap(result) as unknown as Building;
}

/**
 * Delete a Building
 */
export async function deleteBuilding(id: number): Promise<void> {
  const result = await client.DELETE('/api/v2/building/{id}/', {
    params: { path: { id } as never },
  });
  unwrap(result);
}
